import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import ResumeItem from "@/common/components/ResumeItem";
import Skeleton from "@/common/components/Skeleton";
import { equipmentStatusTranslation } from "@/common/constants/equipmentStatus.constants";
import { formatDateTime, formatDecimalUnit } from "@/common/utils/formatters";
import { type EquipmentData as EquipmentDataType } from "@/features/equipments/types/equipments.type";
import { CarIcon } from "lucide-react";

interface EquipmentDataProps {
  equipment?: EquipmentDataType;
}

const EquipmentDataType = ({ equipment }: EquipmentDataProps) => {
  const categoryFieldForDisplayName = equipment?.model?.category?.field_for_display;

  const displayName = (categoryFieldForDisplayName && equipment?.[categoryFieldForDisplayName as keyof typeof equipment] as string) ||
    equipment?.license_plate ||
    equipment?.official_name ||
    equipment?.serial_number ||
    'Não informado';

  return (
    <div className="flex rounded-lg overflow-hidden bg-white-100 min-h-36">
      <ImageCarousel
        isLoading={!equipment}
        files={equipment?.photos.map((photo) => ({ id: photo.id, file: photo.url })) || []}
        className="size-36 border-none"
        showRemoveButton={false}
        Placeholder={(
          <ImageWithPlaceholder
            alt={equipment?.model?.display_name}
            placeholderIcon={CarIcon}
            className="size-36 rounded-none bg-white-800"
            iconClassName="size-32 border-n"
          />
        )}
        showExpandButton
        showNavigationButtons
      />

      <div className="flex-1 p-4">
        <div className="flex flex-col gap-1 h-full">
          <p className="text-sm font-bold">
            {equipment ? displayName : <Skeleton className="w-32 h-5" />}
          </p>

          <p className="text-xs">
            {equipment ? (
              <>
                {equipment?.model?.brand || 'Não informado'} - {equipment?.model?.display_name} - {equipment?.model?.year}
              </>
            ) : <Skeleton className="w-40 h-4" />}
          </p>

          <div className="flex gap-4 my-auto">
            {(equipment?.status || !equipment) && (
              <ResumeItem
                label="Status"
                value={equipment?.status
                  ? equipmentStatusTranslation[(equipment.status) as keyof typeof equipmentStatusTranslation]
                  : '--'}
                isLoading={!equipment}
                labelClassName="text-xs"
                valueClassName="text-sm"
              />
            )}

            <ResumeItem
              label="Filial"
              value={equipment?.branch
                ? equipment?.branch.display_name || '--'
                : '--'}
              isLoading={!equipment}
              labelClassName="text-xs"
              valueClassName="text-sm"
            />

            <ResumeItem
              label={equipment?.model?.category?.unit_for_consumption === 'hours' ? 'Horas' : 'Quilometragem'}
              value={equipment?.last_usage_value
                ? (
                  <div className="flex items-baseline gap-1">
                    <span>
                      {formatDecimalUnit(equipment.last_usage_value, equipment.model?.category?.unit_for_consumption)}
                    </span>

                    {equipment.last_usage_at && (
                      <span className="font-light italic text-xs">
                        {formatDateTime(new Date(equipment.last_usage_at))}
                      </span>
                    )}
                  </div>
                )
                : '--'}
              isLoading={!equipment}
              labelClassName="text-xs"
              valueClassName="text-sm"
            />
          </div>

          <p className='text-xs'>
            {equipment ? (
              <>
                Cadastrado em {formatDateTime(new Date(equipment?.created_at || 0))}
              </>
            ) : <Skeleton className="w-40 h-4" />}
          </p>
        </div>
      </div>
    </div>
  )
}

export default EquipmentDataType;
